import { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import Select from "react-select";

// Custom Styles
const customStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid hsl(var(--border))`,
    borderRadius: "0.375rem",
    backgroundColor: state.isDisabled ? "hsl(var(--muted))" : "hsl(var(--background-card))",
    boxShadow: state.isFocused ? "0 0 0 2px hsl(var(--ring) / 0.3)" : "none",
    "&:hover": {
      borderColor: state.isDisabled ? "hsl(var(--border))" : "hsl(var(--ring))",
    },
    cursor: state.isDisabled ? "not-allowed" : "default",
    opacity: state.isDisabled ? 0.7 : 1,
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: "hsl(var(--popover))",
    borderRadius: "0.375rem",
    border: "1px solid hsl(var(--border))",
    boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "hsl(var(--primary))" : state.isFocused ? "hsl(var(--accent))" : "transparent",
    color: state.isSelected ? "hsl(var(--primary-foreground))" : state.isFocused ? "hsl(var(--accent-foreground))" : "hsl(var(--foreground))",
    "&:active": {
      backgroundColor: "hsl(var(--accent))",
    },
  }),
  groupHeading: (provided) => ({
    ...provided,
    backgroundColor: "hsl(var(--muted))",
    color: "hsl(var(--muted-foreground))",
    padding: "8px 12px",
    fontWeight: 600,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "hsl(var(--secondary))",
    borderRadius: "0.375rem",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "hsl(var(--secondary-foreground))",
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "hsl(var(--secondary-foreground))",
    "&:hover": {
      backgroundColor: "hsl(var(--destructive))",
      color: "hsl(var(--destructive-foreground))",
    },
  }),
  input: (provided) => ({
    ...provided,
    color: "hsl(var(--foreground))",
  }),
  placeholder: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "hsl(var(--muted-foreground) / 0.7)" : "hsl(var(--muted-foreground))",
  }),
  singleValue: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? "hsl(var(--muted-foreground))" : "hsl(var(--foreground))",
  }),
};

export default function AppSelect({
  name,
  value,
  onChange,
  multiple = false,
  options = [],
  loading = false,
  required = false,
  locale = false,
  grouped: _grouped = false,
  preferred = [],
  ...props
}) {
  const intl = useIntl();

  const _t = (id) => intl.formatMessage({ id });

  const grouped = _grouped || preferred?.length > 0;

  // Render Options
  const renderOptions = useCallback(() => {
    /** */

    // Action Options
    const actionOptions = [
      { code: "select_all", label: _t("T.SELECT.ALL"), value: "__select_all__" },
      { code: "select_reverse", label: _t("T.SELECT.REVERSE"), value: "__select_reverse__" },
    ].filter(({ code }) => (code === "select_reverse" ? value?.length > 0 : true));

    // Handle Preferred Options
    if (preferred?.length > 0) {
      /** */

      const preferredOptions = options.filter((opt) => preferred.includes(opt.value)); // Preferred Options
      const otherOptions = options.filter((opt) => !preferred.includes(opt.value)); // Other Options

      return [
        {
          label: _t("T.MOST_USED"),
          options: preferredOptions.map((opt) => ({
            label: locale ? _t(opt.label ?? "-") : opt.label,
            value: opt.value,
          })),
        },
        {
          label: _t("T.ALL"),
          options: otherOptions.map((opt) => ({
            label: locale ? _t(opt.label ?? "-") : opt.label,
            value: opt.value,
          })),
        },
        {
          label: _t("T.ACTIONS"),
          options: actionOptions,
        },
      ];
    }

    // Grouped Options
    else if (grouped) {
      return [
        ...options.map((group) => ({
          label: _t(group.label ?? "-"),
          options: group.options.map((opt) => ({
            label: _t(opt.label ?? "-"),
            value: opt.value,
          })),
        })),
        {
          label: _t("T.ACTIONS"),
          options: actionOptions,
        },
      ];
    }

    // Normal Options

    let _options = [];

    if (props?.placeholder && !multiple) _options.push({ label: props.placeholder, value: null }); // Add Placeholder If Provided (Single Mode)

    if (multiple) _options.push(...actionOptions); // Multiple Mode ? Append Action Options

    // Regular Options
    const regularOptions = options?.map(({ label, value }) => ({
      label: _t(label ?? "-"),
      value,
    }));

    return [..._options, ...regularOptions];

    /** */
  }, [options, multiple, value?.length, locale, intl, preferred?.length]);

  // Render Options
  const _options = useMemo(() => renderOptions(), [renderOptions]);

  // Flattened Options (Flatten In Case Of Grouped Mode)
  const __options = useMemo(() => (!grouped ? _options : _options.flatMap((group) => group.options)), [_options, grouped]);

  // Find Selected Value
  const findSelectedValue = useCallback(() => {
    return multiple ? __options.filter((option) => value?.includes(option.value)) : __options?.find((option) => option.value === value) ?? null;
  }, [__options, value, multiple]);

  // Handle Change
  const handleChange = useCallback(
    (selectedOptions) => {
      /** */

      // Multiple Mode
      if (!multiple) return onChange(selectedOptions?.value);

      // Handle array of selections
      if (Array.isArray(selectedOptions)) {
        /** */

        const lastSelected = selectedOptions[selectedOptions.length - 1];

        // Get all possible values excluding special options
        const getAllPossibleValues = () => {
          return __options.filter((opt) => opt.value !== "__select_all__" && opt.value !== "__select_reverse__" && opt.value !== null).map((opt) => opt.value);
        };

        // Handle Select All
        if (lastSelected?.value === "__select_all__") {
          const allValues = getAllPossibleValues();
          onChange(allValues);
          return;
        }

        // Handle Select Reverse
        if (lastSelected?.value === "__select_reverse__") {
          const allPossibleValues = getAllPossibleValues();
          const reversedSelection = allPossibleValues.filter((val) => !value?.includes(val));
          onChange(reversedSelection);
          return;
        }

        // Regular multi-select
        onChange(selectedOptions.map(({ value }) => value));

        /** */
      }
    },
    [multiple, onChange, __options, value, grouped]
  );

  const selectedValue = useMemo(() => findSelectedValue(), [findSelectedValue]);

  return (
    <Select
      styles={customStyles}
      isDisabled={props.disabled}
      instanceId={name}
      options={_options}
      isOptionDisabled={required ? ({ value }) => !Boolean(value) : undefined}
      onChange={handleChange}
      value={selectedValue}
      isLoading={loading}
      isMulti={multiple}
      {...props}
    />
  );
}
